/* eslint-disable no-console */
import { CHATBOT_SCRIPT_ELEMENT_ID, STORAGE_KEY, DRAG_THRESHOLD } from "./chatbot.constants";

type ChatbotInitParams = {
  firstName: string;
  lastName: string;
  email: string;
  integrationKey: string;
  platformName: string;
  messageId: string;
  secretUrl: string;
  disabled?: boolean;
};

/**
 * Initialize the embedded Salesforce chatbot after the script loads.
 */
function initEmbeddedMessagingCore({
  firstName,
  lastName,
  email,
  integrationKey,
  platformName,
  messageId,
  secretUrl,
}: ChatbotInitParams): void {
  try {
    if (window.embeddedservice_bootstrap) {
      const bootstrap = window.embeddedservice_bootstrap;
      bootstrap.settings.language = "en_US";

      window.addEventListener("onEmbeddedMessagingReady", () => {
        console.log("Einstein Bot is ready…");

        if (bootstrap.prechatAPI) {
          bootstrap.prechatAPI.setHiddenPrechatFields({
            FirstName: firstName,
            LastName: lastName,
            Email: email,
            Suite: "Budgeting_Planning",
          });
        } else {
          console.error("embeddedservice_bootstrap.prechatAPI is not available.");
        }

        attachDraggableBotButton();
      });

      bootstrap.init(integrationKey, platformName, messageId, {
        scrt2URL: secretUrl,
      });
    } else {
      console.error("embeddedservice_bootstrap is not defined on window.");
    }
  } catch (err) {
    console.error("Error loading Embedded Messaging: ", err);
  }
}

/**
 * Add drag and click-suppression logic to the bot button once it's rendered.
 */
function attachDraggableBotButton(): void {
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 500; // 500ms between retries
  let retryCount = 0;

  const tryAttachDraggable = () => {
    const chatWrapper = document.getElementById("embeddedMessagingConversationButton");

    if (!chatWrapper) {
      if (retryCount < MAX_RETRIES) {
        retryCount++;
        console.log(`[ChatBot] Bot button not found, retrying (${retryCount}/${MAX_RETRIES})...`);
        setTimeout(tryAttachDraggable, RETRY_DELAY);
        return;
      } else {
        console.warn("[ChatBot] Bot button not found after maximum retries");
        return;
      }
    }

    let isDragging = false;
    let initialCursorX = 0;
    let initialCursorY = 0;
    let dx = 0;
    let dy = 0;
    let moved = false;

    // Restore previous position if saved
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const position = JSON.parse(saved) as { x: number; y: number };
        dx = position.x;
        dy = position.y;
        chatWrapper.style.transform = `translate(${dx}px, ${dy}px)`;
      } catch (e) {
        console.warn("[ChatBot] Invalid saved position");
      }
    }

    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      initialCursorX = e.clientX - dx;
      initialCursorY = e.clientY - dy;
      moved = false;
      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      dx = e.clientX - initialCursorX;
      dy = e.clientY - initialCursorY;

      if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
        moved = true;
        chatWrapper.style.transform = `translate(${dx}px, ${dy}px)`;
      }
    };

    const onMouseUp = () => {
      isDragging = false;

      if (moved) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ x: dx, y: dy }));
      }

      setTimeout(() => {
        moved = false;
      }, 100);
    };

    const onClick = (e: MouseEvent) => {
      if (moved) {
        e.stopPropagation();
        e.preventDefault();
        console.log("[ChatBot] Click suppressed due to drag");
      }
    };

    chatWrapper.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    chatWrapper.addEventListener("click", onClick, true);

    console.log("[ChatBot] Draggable behavior enabled with position persistence");
  };

  // Start the initial attempt
  tryAttachDraggable();
}

/**
 * Append the Salesforce chatbot script to the page and initialize it.
 */
export function appendChatBotSnippet(params: ChatbotInitParams): void {
  const { integrationKey, platformName, messageId, secretUrl, disabled = false } = params;

  if (disabled) {
    console.log("Chatbot is disabled. Skipping initialization.");
    return;
  }

  if (!integrationKey || !platformName || !messageId || !secretUrl) {
    console.error("Missing required chatbot configuration values.");
    return;
  }

  if (document.getElementById(CHATBOT_SCRIPT_ELEMENT_ID)) {
    console.warn("Chatbot script already exists on the page. Skipping append.");
    return;
  }

  window.initEmbeddedMessaging = () => {
    initEmbeddedMessagingCore(params);
  };

  const scriptElement = document.createElement("script");
  scriptElement.id = CHATBOT_SCRIPT_ELEMENT_ID;
  scriptElement.type = "text/javascript";
  scriptElement.src = `${messageId}/assets/js/bootstrap.min.js`;

  scriptElement.setAttribute("onload", "initEmbeddedMessaging()");
  document.body.appendChild(scriptElement);
}

/**
 * Remove the Salesforce chatbot script and globals (cleanup).
 */
export function removeChatBotSnippet(): void {
  const scriptElement = document.getElementById(CHATBOT_SCRIPT_ELEMENT_ID);
  if (scriptElement?.parentNode) {
    scriptElement.parentNode.removeChild(scriptElement);
  }

  if (window.initEmbeddedMessaging) {
    delete window.initEmbeddedMessaging;
  }
}
